// components/ui/Dropdown.tsx
import { useState, useRef, useEffect, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

interface DropdownItem {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

interface DropdownProps {
  trigger: ReactNode;
  items: (DropdownItem | 'divider')[];
  align?: 'left' | 'right';
  className?: string;
}

export function Dropdown({ trigger, items, align = 'left', className }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn('relative inline-block text-left', className)} ref={dropdownRef}>
      <div onClick={() => setOpen(!open)}>
        {trigger}
      </div>

      {open && (
        <div
          className={cn(
            'absolute z-50 mt-2 w-56 origin-top-right rounded-lg bg-surface shadow-lg border border-border',
            'focus:outline-none py-1 animate-scale-in',
            align === 'right' ? 'right-0' : 'left-0'
          )}
        >
          {items.map((item, index) => {
            if (item === 'divider') {
              return <div key={`divider-${index}`} className="my-1 h-px bg-border-subtle" />;
            }

            return (
              <button
                key={item.id}
                onClick={() => {
                  if (!item.disabled) {
                    item.onClick();
                    setOpen(false);
                  }
                }}
                disabled={item.disabled}
                className={cn(
                  'flex w-full items-center gap-2 px-4 py-2 text-body-sm text-left',
                  'transition-colors duration-fast',
                  item.danger
                    ? 'text-error hover:bg-error-subtle'
                    : 'text-text hover:bg-bg-subtle',
                  item.disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {item.icon}
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Simple dropdown button variant
interface DropdownButtonProps {
  label: string;
  items: (DropdownItem | 'divider')[];
  className?: string;
}

export function DropdownButton({ label, items, className }: DropdownButtonProps) {
  return (
    <Dropdown
      className={className}
      trigger={
        <button className={cn(
          'inline-flex items-center gap-2 px-4 py-2',
          'text-body-sm font-medium text-text',
          'bg-surface border-[1.5px] border-border rounded-md',
          'hover:bg-bg-subtle hover:border-border-strong',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'transition-all duration-fast'
        )}>
          {label}
          <ChevronDown size={16} />
        </button>
      }
      items={items}
    />
  );
}
